import React, { useState, useRef } from 'react';
import { Search, Music, Volume2, Play, Pause, X, Crown, Headphones, SkipBack, SkipForward, Scissors, TrendingUp, Bookmark, BookmarkCheck, ExternalLink, GripHorizontal, Edit3 } from 'lucide-react';

interface AudioItem {
  id: string;
  title: string;
  artist: string;
  duration: number;
  url: string;
  waveform: number[];
  type: 'song' | 'sound';
  isOriginal?: boolean;
  creatorId?: string;
  creatorName?: string;
  royaltyRate?: number;
  tags?: string[];
  isPixabay?: boolean;
  pixabayId?: string;
  isTrending?: boolean;
  plays?: number;
  position?: number; // For timeline positioning
}

interface AudioSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAudio: (audio: AudioItem & { trimStart?: number; trimEnd?: number; position?: number }) => void;
  currentAudio?: AudioItem;
}

// Mock data for demonstration
const MOCK_AUDIO_DATA: AudioItem[] = [
  {
    id: '1',
    title: 'Summer Vibes',
    artist: 'DJ Cool',
    duration: 180,
    url: '/audio/summer-vibes.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 100),
    type: 'song',
    tags: ['upbeat', 'summer', 'electronic']
  },
  {
    id: '2',
    title: 'Ocean Waves',
    artist: 'Nature Sounds',
    duration: 120,
    url: '/audio/ocean-waves.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 60),
    type: 'sound',
    tags: ['nature', 'relaxing', 'ambient']
  },
  {
    id: '3',
    title: 'City Night',
    artist: 'UserCreator123',
    duration: 95,
    url: '/audio/city-night.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 80),
    type: 'sound',
    isOriginal: true,
    creatorId: 'user123',
    creatorName: 'UserCreator123',
    royaltyRate: 0.15,
    tags: ['urban', 'night', 'ambient']
  },
  {
    id: '4',
    title: 'Trending Beat',
    artist: 'BeatMaker',
    duration: 200,
    url: '/audio/trending-beat.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 90),
    type: 'song',
    tags: ['trending', 'hip-hop', 'beat']
  },
  {
    id: '5',
    title: 'Rain Drops',
    artist: 'SoundCreator456',
    duration: 150,
    url: '/audio/rain-drops.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 70),
    type: 'sound',
    isOriginal: true,
    creatorId: 'user456',
    creatorName: 'SoundCreator456',
    royaltyRate: 0.12,
    tags: ['rain', 'nature', 'peaceful']
  }
];

// Mock trending audio data
const TRENDING_AUDIO: AudioItem[] = [
  {
    id: 'trending-1',
    title: 'Viral Beat 2024',
    artist: 'TrendMaster',
    duration: 95,
    url: 'https://cdn.pixabay.com/audio/2024/01/15/audio_viral_beat.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 100),
    type: 'song',
    isTrending: true,
    plays: 1250000,
    tags: ['viral', 'trending', 'upbeat']
  },
  {
    id: 'trending-2',
    title: 'Chill Vibes',
    artist: 'LoFi King',
    duration: 120,
    url: 'https://cdn.pixabay.com/audio/2024/01/10/audio_chill_vibes.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 80),
    type: 'song',
    isTrending: true,
    plays: 980000,
    tags: ['chill', 'lofi', 'trending']
  },
  {
    id: 'trending-3',
    title: 'Epic Cinematic',
    artist: 'FilmScore Pro',
    duration: 180,
    url: 'https://cdn.pixabay.com/audio/2024/01/08/audio_epic_cinematic.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 120),
    type: 'sound',
    isTrending: true,
    plays: 750000,
    tags: ['cinematic', 'epic', 'dramatic']
  }
];

// Mock Pixabay audio data
const PIXABAY_AUDIO: AudioItem[] = [
  {
    id: 'pixabay-1',
    title: 'Acoustic Guitar Melody',
    artist: 'Pixabay Audio',
    duration: 145,
    url: 'https://cdn.pixabay.com/audio/2023/12/15/audio_acoustic_guitar.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 90),
    type: 'song',
    isPixabay: true,
    pixabayId: 'acoustic-guitar-145',
    tags: ['acoustic', 'guitar', 'peaceful']
  },
  {
    id: 'pixabay-2',
    title: 'Nature Ambience',
    artist: 'Pixabay Audio',
    duration: 200,
    url: 'https://cdn.pixabay.com/audio/2023/11/20/audio_nature_ambience.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 60),
    type: 'sound',
    isPixabay: true,
    pixabayId: 'nature-ambience-200',
    tags: ['nature', 'ambient', 'relaxing']
  },
  {
    id: 'pixabay-3',
    title: 'Electronic Beat',
    artist: 'Pixabay Audio',
    duration: 110,
    url: 'https://cdn.pixabay.com/audio/2023/10/30/audio_electronic_beat.mp3',
    waveform: Array.from({length: 100}, () => Math.random() * 110),
    type: 'song',
    isPixabay: true,
    pixabayId: 'electronic-beat-110',
    tags: ['electronic', 'upbeat', 'modern']
  }
];

const AudioSearchDialog: React.FC<AudioSearchDialogProps> = ({
  isOpen,
  onClose,
  onSelectAudio,
  currentAudio
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'songs' | 'sounds' | 'trending' | 'pixabay' | 'saved'>('all');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<AudioItem | null>(null);
  const [showTrimControls, setShowTrimControls] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(30);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [savedAudio, setSavedAudio] = useState<Set<string>>(new Set());
  const [audioPosition, setAudioPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartPosition, setDragStartPosition] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);

  // Combine all audio sources
  const getAllAudio = () => {
    return [...MOCK_AUDIO_DATA, ...TRENDING_AUDIO, ...PIXABAY_AUDIO];
  };

  // Filter audio based on search and tab
  const filteredAudio = (() => {
    let audioData = [];
    
    switch (activeTab) {
      case 'trending':
        audioData = TRENDING_AUDIO;
        break;
      case 'pixabay':
        audioData = PIXABAY_AUDIO;
        break;
      case 'saved':
        audioData = getAllAudio().filter(audio => savedAudio.has(audio.id));
        break;
      case 'all':
        audioData = getAllAudio();
        break;
      case 'songs':
        audioData = getAllAudio().filter(audio => audio.type === 'song');
        break;
      case 'sounds':
        audioData = getAllAudio().filter(audio => audio.type === 'sound');
        break;
      default:
        audioData = getAllAudio();
    }

    return audioData.filter(audio => {
      const matchesSearch = audio.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           audio.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           audio.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesSearch;
    });
  })();

  // Touch handlers for swipeable dialog
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX || !touchStartY) return;
    
    const touchEndX = e.touches[0].clientX;
    const touchEndY = e.touches[0].clientY;
    const deltaX = touchStartX - touchEndX;
    const deltaY = touchStartY - touchEndY;
    
    // Only handle horizontal swipes that are more significant than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      // Swipe left to go to next tab, swipe right to go to previous tab
      const tabs = ['all', 'trending', 'pixabay', 'songs', 'sounds', 'saved'];
      const currentIndex = tabs.indexOf(activeTab);
      
      if (deltaX > 0 && currentIndex < tabs.length - 1) {
        // Swipe left - next tab
        setActiveTab(tabs[currentIndex + 1] as any);
      } else if (deltaX < 0 && currentIndex > 0) {
        // Swipe right - previous tab
        setActiveTab(tabs[currentIndex - 1] as any);
      }
      
      setTouchStartX(0);
      setTouchStartY(0);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartX(0);
    setTouchStartY(0);
  };

  // Timeline drag handlers
  const handleTimelineDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartPosition(audioPosition);
  };

  const handleTimelineDragMove = (e: React.MouseEvent) => {
    if (!isDragging || !timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStartX;
    const timelineWidth = rect.width;
    const maxDuration = selectedAudio?.duration || 30;
    const deltaTime = (deltaX / timelineWidth) * maxDuration;
    
    const newPosition = Math.max(0, Math.min(maxDuration - (trimEnd - trimStart), dragStartPosition + deltaTime));
    setAudioPosition(newPosition);
  };

  const handleTimelineDragEnd = () => {
    setIsDragging(false);
  };

  // Save/unsave audio
  const toggleSaveAudio = (audioId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSavedAudio = new Set(savedAudio);
    if (savedAudio.has(audioId)) {
      newSavedAudio.delete(audioId);
    } else {
      newSavedAudio.add(audioId);
    }
    setSavedAudio(newSavedAudio);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = (audio: AudioItem) => {
    if (playingId === audio.id) {
      // Pause current audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingId(null);
    } else {
      // Stop any currently playing audio
      if (playingId && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Play new audio
      if (!audioRef.current) {
        audioRef.current = new Audio(audio.url);
        audioRef.current.addEventListener('ended', () => {
          setPlayingId(null);
        });
        audioRef.current.addEventListener('error', () => {
          console.warn(`Audio file not found: ${audio.url}`);
          setPlayingId(null);
        });
      }
      
      audioRef.current.play().catch(() => {
        console.warn(`Failed to play audio: ${audio.url}`);
        setPlayingId(null);
      });
      setPlayingId(audio.id);
    }
  };

  const renderWaveform = (waveform: number[], isPlaying: boolean) => {
    return (
      <div className="flex items-end space-x-0.5 h-8 w-20">
        {waveform.slice(0, 20).map((height, index) => (
          <div
            key={index}
            className={`w-1 rounded-sm transition-colors ${
              isPlaying ? 'bg-primary-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            style={{ height: `${Math.max(height / 4, 4)}px` }}
          />
        ))}
      </div>
    );
  };

  const handleSelectAudio = (audio: AudioItem) => {
    setSelectedAudio(audio);
    setShowTrimControls(true);
    setTrimStart(0);
    setTrimEnd(Math.min(30, duration || 30));
    
    // Load audio to get duration
    if (audioRef.current) {
      audioRef.current.src = audio.url;
      audioRef.current.addEventListener('loadedmetadata', () => {
        const audioDuration = audioRef.current?.duration || 30;
        setDuration(audioDuration);
        setTrimEnd(Math.min(30, audioDuration));
      });
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });
    }
  };

  const handleQuickSelect = (audio: AudioItem) => {
    onSelectAudio({
      ...audio,
      trimStart: 0,
      trimEnd: Math.min(30, audio.duration),
      position: 0
    });
    onClose();
  };

  const handleTrimStartChange = (value: number) => {
    const newStart = Math.max(0, Math.min(value, trimEnd - 1));
    setTrimStart(newStart);
    if (audioRef.current) {
      audioRef.current.currentTime = newStart;
    }
  };

  const handleTrimEndChange = (value: number) => {
    const newEnd = Math.min(duration, Math.max(value, trimStart + 1));
    setTrimEnd(newEnd);
  };

  const jumpToStart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = trimStart;
    }
  };

  const jumpToEnd = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = trimEnd;
    }
  };

  const confirmSelection = () => {
    if (selectedAudio) {
      onSelectAudio({
        ...selectedAudio,
        trimStart,
        trimEnd,
        position: audioPosition
      });
      onClose();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPlays = (plays: number) => {
    if (plays >= 1000000) {
      return `${(plays / 1000000).toFixed(1)}M`;
    } else if (plays >= 1000) {
      return `${(plays / 1000).toFixed(1)}K`;
    }
    return plays.toString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4">
      <div 
        ref={dialogRef}
        className="bg-white dark:bg-gray-900 w-full max-w-md h-[90vh] max-h-[600px] rounded-2xl shadow-xl transform transition-all overflow-hidden flex flex-col"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center">
            <Headphones className="mr-2 h-5 w-5" />
            Audio Library
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search songs, sounds, or creators..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Enhanced Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
          {[
            { key: 'all', label: 'All', icon: Music },
            { key: 'trending', label: 'Trending', icon: TrendingUp },
            { key: 'pixabay', label: 'Pixabay', icon: ExternalLink },
            { key: 'songs', label: 'Songs', icon: Music },
            { key: 'sounds', label: 'Sounds', icon: Volume2 },
            { key: 'saved', label: 'Saved', icon: Bookmark }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex-shrink-0 flex items-center justify-center py-3 px-4 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === key
                  ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="mr-1 h-4 w-4" />
              {label}
              {key === 'saved' && savedAudio.size > 0 && (
                <span className="ml-1 bg-primary-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {savedAudio.size}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Audio List */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide"
        >
          {filteredAudio.map((audio) => (
            <div
              key={audio.id}
              className={`p-3 rounded-lg border transition-all ${
                currentAudio?.id === audio.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-3">
                {/* Play/Pause Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayPause(audio);
                  }}
                  className="flex-shrink-0 w-10 h-10 bg-primary-600 hover:bg-primary-700 text-white rounded-full flex items-center justify-center transition-colors"
                >
                  {playingId === audio.id ? (
                    <Pause size={16} />
                  ) : (
                    <Play size={16} className="ml-0.5" />
                  )}
                </button>

                {/* Waveform */}
                <div className="flex-shrink-0">
                  {renderWaveform(audio.waveform, playingId === audio.id)}
                </div>

                {/* Audio Info - Clickable for quick selection */}
                <div 
                  className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded p-2 -m-2 transition-colors"
                  onClick={() => handleQuickSelect(audio)}
                >
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                      {audio.title}
                    </h4>
                    {audio.isOriginal && (
                      <Crown size={12} className="text-yellow-500 flex-shrink-0" />
                    )}
                    {audio.isTrending && (
                      <TrendingUp size={12} className="text-red-500 flex-shrink-0" />
                    )}
                    {audio.isPixabay && (
                      <ExternalLink size={12} className="text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {audio.artist}
                  </p>
                  {audio.isOriginal && audio.royaltyRate && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {(audio.royaltyRate * 100).toFixed(0)}% royalty to creator
                    </p>
                  )}
                  {audio.plays && (
                    <p className="text-xs text-gray-400">
                      {formatPlays(audio.plays)} plays
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">
                      {formatDuration(audio.duration)}
                    </span>
                    <div className="flex items-center space-x-1">
                      {audio.tags?.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-1">
                  {/* Edit Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAudio(audio);
                    }}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Edit & Trim"
                  >
                    <Edit3 size={16} />
                  </button>
                  
                  {/* Save Button */}
                  <button
                    onClick={(e) => toggleSaveAudio(audio.id, e)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-primary-500 transition-colors"
                    title="Save for Later"
                  >
                    {savedAudio.has(audio.id) ? (
                      <BookmarkCheck size={16} className="text-primary-500" />
                    ) : (
                      <Bookmark size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredAudio.length === 0 && (
            <div className="text-center py-8">
              <Music className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No audio found matching your search
              </p>
            </div>
          )}
        </div>

        {/* Enhanced Trim Controls with Timeline */}
        {showTrimControls && selectedAudio && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Scissors size={16} className="mr-2" />
                Edit: {selectedAudio.title}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Duration: {formatTime(trimEnd - trimStart)}
              </span>
            </div>
            
            {/* Enhanced Timeline with Draggable Audio Position */}
            <div className="relative mb-4">
              <div 
                ref={timelineRef}
                className="w-full h-12 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden relative cursor-pointer"
                onMouseDown={handleTimelineDragStart}
                onMouseMove={handleTimelineDragMove}
                onMouseUp={handleTimelineDragEnd}
                onMouseLeave={handleTimelineDragEnd}
              >
                {/* Total duration bar */}
                <div className="w-full h-full bg-gray-300 dark:bg-gray-500"></div>
                
                {/* Audio clip representation */}
                <div 
                  className="absolute top-1 h-10 bg-primary-500 rounded flex items-center justify-center cursor-move shadow-lg"
                  style={{
                    left: `${(audioPosition / duration) * 100}%`,
                    width: `${((trimEnd - trimStart) / duration) * 100}%`
                  }}
                >
                  <GripHorizontal size={16} className="text-white" />
                </div>
                
                {/* Current time indicator */}
                <div 
                  className="absolute top-0 w-0.5 h-full bg-white shadow-lg z-10"
                  style={{
                    left: `${(currentTime / duration) * 100}%`
                  }}
                ></div>
              </div>
              
              {/* Time markers */}
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>0:00</span>
                <span>Position: {formatTime(audioPosition)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Trim Start Control */}
            <div className="mb-3">
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                Start Time: {formatTime(trimStart)}
              </label>
              <input
                type="range"
                min={0}
                max={duration}
                step={0.1}
                value={trimStart}
                onChange={(e) => handleTrimStartChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>

            {/* Trim End Control */}
            <div className="mb-4">
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                End Time: {formatTime(trimEnd)}
              </label>
              <input
                type="range"
                min={0}
                max={duration}
                step={0.1}
                value={trimEnd}
                onChange={(e) => handleTrimEndChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={jumpToStart}
                  className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Jump to start"
                >
                  <SkipBack size={16} />
                  <span className="text-xs">Start</span>
                </button>
                <button
                  onClick={jumpToEnd}
                  className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Jump to end"
                >
                  <SkipForward size={16} />
                  <span className="text-xs">End</span>
                </button>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowTrimControls(false)}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSelection}
                  className="bg-primary-600 hover:bg-primary-700 text-white rounded-lg py-2 px-4 text-sm font-medium transition-colors"
                >
                  Add to Timeline
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioSearchDialog;
